<template>
  <div class="sf-commcard sf-commcard-checks sf-below-3" type="checklist">
    <div class="sf-commcard-title sf-uppercase">{{ text }}</div>
    <div>
      <CheckCard
        v-for="check in sortedPendingChecks"
        :key="check.id"
        :allow-any-user="allowAnyUser"
        :open-reply="openReply"
        :show-context-btn="showContextBtn"
        v-bind="check"
        :default-comments-visibility-state="defaultCommentsVisibilityState"
        :show-period="showPeriod"
        @toggle-check-applicable="toggleCheckApplicable"
      ></CheckCard>
    </div>
    <div v-show="sortedCompletedAndNotApplicableChecks.length">
      <div class="sf-commcard-footer">
        <a @click="toggleHiddenChecks">{{ toggleHiddenChecksText }}</a>
      </div>
    </div>
    <div v-show="showingCompletedChecks">
      <CheckCard
        v-for="check in sortedCompletedAndNotApplicableChecks"
        :key="check.id"
        :allow-any-user="allowAnyUser"
        :open-reply="openReply"
        :show-context-btn="showContextBtn"
        v-bind="check"
        :default-comments-visibility-state="defaultCommentsVisibilityState"
        :show-period="showPeriod"
        @toggle-check-applicable="toggleCheckApplicable"
      ></CheckCard>
    </div>
    <ConfirmCheckToggleApplicableModal @confirm="performToggleApplicable" />
  </div>
</template>

<script>
import { pick, orderBy, without } from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'

import CheckCard from './check-card'
import useCheckHelpers from '../../composables/use-check-helpers'
import { SfLog } from '@app/adapters/logger'
import api from '@app/application/communication/sections/communication/api/check'
import ConfirmCheckToggleApplicableModal from './confirm-check-toggle-applicable-modal.vue'

export default {
  components: {
    CheckCard,
    ConfirmCheckToggleApplicableModal,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    ledgerId: {
      type: Number,
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    participantIds: {
      type: Array,
      required: true,
    },
    defaultCommentsVisibilityState: {
      type: String,
      default: 'default',
    },
    showPeriod: {
      type: Boolean,
      default: false,
    },
    allowAnyUser: {
      type: Boolean,
      default: false,
    },
    showContextBtn: {
      type: Boolean,
      default: false,
    },
    openReply: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return {
      ...useCheckHelpers(),
    }
  },
  computed: {
    ...mapState({
      showingCompletedChecksInCheckListIds: (state) =>
        state.ui.showingCompletedChecksInCheckListIds,
      storeLedgerId: (state) => state.ledgerId,
    }),

    ...mapGetters(['checkListChecks']),

    checkIds() {
      return this.checkListChecks(this.ledgerId, this.id).map(
        (check) => check.id
      )
    },

    completedCheckIds() {
      return this.sortedCompletedChecks.map((check) => check.id)
    },

    sortedChecks() {
      return orderBy(
        this.checkListChecks(this.storeLedgerId || this.ledgerId, this.id),
        ['createdAt'],
        ['asc']
      )
    },

    sortedCompletedAndNotApplicableChecks() {
      return this.sortedCompletedChecks.concat(this.sortedNotApplicableChecks)
    },

    sortedCompletedChecks() {
      return this.sortedChecks.filter((check) =>
        this.checkCheckedByAllParticipants(check)
      )
    },

    sortedNotApplicableChecks() {
      return this.sortedChecks.filter((check) =>
        this.checkIsNotApplicable(check)
      )
    },

    sortedPendingChecks() {
      return without(
        this.sortedChecks,
        ...this.sortedCompletedAndNotApplicableChecks
      )
    },

    showingCompletedChecks() {
      return this.showingCompletedChecksInCheckListIds.includes(this.id)
    },

    toggleHiddenChecksText() {
      const completedChecksCount = this.sortedCompletedChecks.length
      const notApplicableChecksCount = this.sortedNotApplicableChecks.length
      const completedAndNotApplicableChecksCount =
        completedChecksCount + notApplicableChecksCount

      if (completedChecksCount === 0) {
        return this.I18n.t(
          `communication.checks.toggle_not_applicable.${this.showingCompletedChecks}`,
          {
            count: completedAndNotApplicableChecksCount,
          }
        )
      }

      if (notApplicableChecksCount === 0) {
        return this.I18n.t(
          `communication.checks.toggle_completed.${this.showingCompletedChecks}`,
          {
            count: completedAndNotApplicableChecksCount,
          }
        )
      }

      return this.I18n.t(
        `communication.checks.toggle_not_applicable_and_completed.${this.showingCompletedChecks}`,
        {
          completed_count: completedChecksCount,
          not_applicable_count: notApplicableChecksCount,
        }
      )
    },
  },
  methods: {
    ...mapActions([
      'toggleCompletedChecksInCheckList',
      'toggleCheckNotApplicable',
    ]),

    toggleHiddenChecks() {
      this.toggleCompletedChecksInCheckList(this.id)
    },

    async toggleCheckApplicable(payload) {
      const params = pick(payload, ['checkLinkId', 'ledgerCheckListId'])

      if (payload.withConfirm) {
        window.modalCitizen.openModalPortal(
          'confirm-check-toggle-applicable-modal',
          params
        )
      } else {
        await this.performToggleApplicable(params)
      }
    },

    async performToggleApplicable({ checkLinkId, ledgerCheckListId }) {
      try {
        await api.toggleCheckApplicable(ledgerCheckListId, checkLinkId)
        this.toggleCheckNotApplicable(checkLinkId)
      } catch (error) {
        SfLog.error('Failed to toggle check applicable', { error })
      }
    },
  },
}
</script>
